import { Alert, Button, Modal } from "flowbite-react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { ADMIN, QUERIES, TAGS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import Input from "../inputs/Input";
import DrawerLoading from "../DrawerLoading";
import SelectInput from "../inputs/SelectInput";
import { formatToReal, normalizeText } from "../../utils/utils";
import CurrencyInput from "../inputs/CurrencyInput";
import { FaRegQuestionCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import { RiRobot2Line } from "react-icons/ri";
import MultiSelectPopover from "../inputs/MultiSelectPopover";

const CreateQueryForm = ({
  categories,
  company,
  fetchQueries,
  onClose,
  isOpen,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ---- TAGS state (novo) ----
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);

  // categorias
  const {
    isLoading: isLoadingAdditionalQueries,
    error: errorAdditionalQueries,
    data: additionalQueries,
  } = useQuery(["additionalQueries"], async () => {
    const { data } = await axios.get(
      `${ADMIN.GET_QUERIES}?isModular=0&isAdditional=1`
    );
    return data?.data?.data ?? [];
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      query: {
        isAdditional: false, // 👈 default
      },
      fields: [
        {
          priority: 1,
          queryId: "",
          providerId: "",
          ignoreErrors: true,
        },
      ],
    },
  });

  const isAdditional = watch("query.isAdditional");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "fields",
  });

  // --------- Providers (já existia) ----------
  const { isLoading: isLoadingProviders, data: providers } = useQuery(
    ["providers", company, isOpen],
    async () => {
      const { data: items } = await axios.get(`${ADMIN.GET_PROVIDERS_ALL}`);
      return items.data;
    },
    { enabled: isOpen }
  );

  // --------- Companies ----------
  const { isLoading: isLoadingCompanies, data: companiesPayload } = useQuery(
    ["companiesOptions", isOpen],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`
      );
      return data?.data?.data ?? data?.data ?? [];
    },
    { enabled: isOpen }
  );

  const companiesOptions = useMemo(() => {
    const seen = new Set();
    const list =
      (companiesPayload || [])
        .filter((c) => {
          const typeId = String(c?.companyTypeId || "").toUpperCase();
          const typeName = String(c?.CompanyType?.name || "").toUpperCase();
          return typeId.includes("REPRESENT") || typeName.includes("REPRESENT");
        })
        .map((c) => {
          const name =
            c?.typeCustomer === "CPF"
              ? c?.name
              : c?.socialReason || c?.name || c?.companyName;
          return { id: c.id, name: name || `#${c.id}` };
        }) ?? [];
    return list
      .filter((x) => x.id != null)
      .filter((x) => {
        if (seen.has(x.id)) return false;
        seen.add(x.id);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companiesPayload]);

  // --------- Tags (novo) ----------
  const {
    isLoading: isLoadingTags,
    data: tagsPayload,
    error: tagsError,
  } = useQuery(
    ["admin-tags", isOpen],
    async () => {
      // lista simples, sem paginação (conforme rotas que criamos)
      const { data } = await axios.get(`${TAGS.GET_ALL_SIMPLE}`);
      // sua API costuma responder { status, data }, então ajusto:
      return Array.isArray(data?.data) ? data.data : data;
    },
    { enabled: isOpen }
  );

  const allTags = useMemo(() => tagsPayload || [], [tagsPayload]);

  // filtro local por nome
  const filteredTags = useMemo(() => {
    const s = tagSearch.trim().toLowerCase();
    if (!s) return allTags;
    return allTags.filter((t) => t.name.toLowerCase().includes(s));
  }, [allTags, tagSearch]);

  // slug automático
  useEffect(() => {
    setValue("query.url", normalizeText(watch("query.name") || ""));
  }, [watch("query.name"), setValue]);

  // prioridade automática
  useEffect(() => {
    fields.forEach((_, index) => {
      setValue(`fields[${index}].priority`, index + 1);
    });
  }, [fields, setValue]);

  // helpers de tag
  const toggleTag = (id) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // --- Transfer list: consultas adicionais (quando NÃO é adicional) ---
  const [selectedAdditionalIds, setSelectedAdditionalIds] = useState([]);
  const [leftSelected, setLeftSelected] = useState([]); // ids marcados na lista esquerda
  const [rightSelected, setRightSelected] = useState([]); // ids marcados na lista direita
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");

  const categoryId = watch("query.categoryId");

  const candidateAdditionals = useMemo(() => {
    const list = Array.isArray(additionalQueries) ? additionalQueries : [];
    // a rota já vem com isAdditional=1, mas mantemos a guarda
    return list
      .filter(
        (q) => q?.isAdditional && String(q?.categoryId) === String(categoryId)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [additionalQueries, categoryId]);

  const leftList = useMemo(() => {
    const term = searchLeft.trim().toLowerCase();
    return candidateAdditionals
      .filter((q) => !selectedAdditionalIds.includes(q.id))
      .filter((q) => (term ? q.name.toLowerCase().includes(term) : true));
  }, [candidateAdditionals, selectedAdditionalIds, searchLeft]);

  const rightList = useMemo(() => {
    const term = searchRight.trim().toLowerCase();
    return selectedAdditionalIds
      .map((id) => candidateAdditionals.find((q) => q.id === id))
      .filter(Boolean)
      .filter((q) => (term ? q.name.toLowerCase().includes(term) : true));
  }, [candidateAdditionals, selectedAdditionalIds, searchRight]);

  // mover itens
  const moveRight = () => {
    setSelectedAdditionalIds((prev) =>
      Array.from(new Set([...prev, ...leftSelected]))
    );
    setLeftSelected([]);
  };
  const moveLeft = () => {
    setSelectedAdditionalIds((prev) =>
      prev.filter((id) => !rightSelected.includes(id))
    );
    setRightSelected([]);
  };
  const moveAllRight = () => {
    setSelectedAdditionalIds((prev) =>
      Array.from(new Set([...prev, ...leftList.map((q) => q.id)]))
    );
    setLeftSelected([]);
  };
  const moveAllLeft = () => {
    setSelectedAdditionalIds([]);
    setRightSelected([]);
  };

  // limpar se virar "adicional"
  useEffect(() => {
    if (watch("query.isAdditional")) {
      setSelectedAdditionalIds([]);
      setLeftSelected([]);
      setRightSelected([]);
      setSearchLeft("");
      setSearchRight("");
    }
  }, [watch("query.isAdditional")]);

  const selectAllFiltered = () => {
    const onlyIds = filteredTags.map((t) => t.id);
    setSelectedTagIds((prev) => {
      const set = new Set(prev);
      onlyIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  const clearAllTags = () => setSelectedTagIds([]);

  useEffect(() => {
    if (!isAdditional) {
      setSelectedTagIds([]);
      setTagSearch("");
    }
  }, [isAdditional]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError(null);

    // valida duplicados nos conteúdos
    const queryIds = data.fields.map((f) => f.queryId);
    const duplicates = queryIds.filter(
      (id, idx) => queryIds.indexOf(id) !== idx && id
    );
    if (duplicates.length > 0) {
      duplicates.forEach((dupId) => {
        data.fields.forEach((field, i) => {
          if (field.queryId === dupId) {
            setError(`fields.${i}.queryId`, {
              type: "manual",
              message: "Conteúdo duplicado",
            });
          }
        });
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // 🔹 Envia tudo de uma vez: dados da consulta + tags selecionadas
      await axios.post(`${ADMIN.GET_CUSTOMIZED_QUERY}`, {
        ...data,
        tagIds: selectedTagIds,
        additionalQueryIds: isAdditional ? [] : selectedAdditionalIds,
        companyIds: selectedCompanyIds,
      });

      onClose(false);
      fetchQueries?.();
      reset();
      setSelectedTagIds([]);
      setSelectedCompanyIds([]);
      setSelectedAdditionalIds([]);
      setTagSearch("");
    } catch (error) {
      setSubmitError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toCents = (v) => {
    if (v == null) return 0;
    const s = String(v).trim();

    // Se tiver vírgula, tratamos formato PT-BR: 1.234,56 -> 1234.56
    const normalized = s.includes(",")
      ? s.replace(/\./g, "").replace(",", ".")
      : s; // "1.00" permanece "1.00"

    const n = parseFloat(normalized);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100);
  };

  const watchedFields = useWatch({ control, name: "fields" }) || [];

  // Índice: "providerId:queryId" -> price (em centavos)
  const priceIndexCents = useMemo(() => {
    const map = new Map();
    (providers || []).forEach((p) => {
      (p?.associatedQueries || []).forEach((q) => {
        const key = `${Number(p.id)}:${Number(q.id)}`;
        const raw = q?.QueryProvider?.price; // ex.: "1.00"
        map.set(key, toCents(raw));
      });
    });
    return map;
  }, [providers]);

  // Soma dos preços selecionados (em centavos) -> converte para reais no final
  const costPrice = useMemo(() => {
    if (!Array.isArray(watchedFields) || priceIndexCents.size === 0) return 0;

    const sumCents = watchedFields.reduce((sum, f) => {
      const key = `${Number(f?.providerId)}:${Number(f?.queryId)}`;
      return sum + (priceIndexCents.get(key) || 0);
    }, 0);

    return sumCents / 100; // volta para reais
  }, [watchedFields, priceIndexCents]);

  return (
    <Modal show={isOpen} onClose={() => onClose(false)} size="5xl">
      <Modal.Header className="py-2 flex items-center bg-gray-50">
        <span className="text-base">Criar nova consulta</span>
      </Modal.Header>
      <Modal.Body>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <Input
            register={register}
            label="Nome da consulta *"
            name="query.name"
            required={true}
          />
          <Input
            register={register}
            label="Descrição da consulta"
            name="query.description"
          />

          <div className="flex flex-col md:flex-row gap-4 md:gap-2">
            <div className="flex-1">
              <Input
                register={register}
                label="URL amigável *"
                name="query.url"
                required={true}
              />
              <small style={{ fontSize: "10px" }}>
                Obs: Preenchida automaticamente
              </small>
            </div>
            <div className="flex-1">
              <CurrencyInput
                register={register}
                label="Preço da consulta *"
                name="query.price"
                required={true}
                control={control}
              />
              <div className="mt-1 text-xs text-gray-600">
                <span className="font-medium">Preço de custo:</span>{" "}
                {formatToReal(costPrice)}
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 md:gap-2">
            <SelectInput
              register={register}
              name="query.categoryId"
              label="Categoria da consulta *"
              required={true}
            >
              <option value="">Selecione</option>
              {categories?.map((category, index) => (
                <option key={index} value={category.id}>
                  {category.name}
                </option>
              ))}
            </SelectInput>
            <SelectInput
              register={register}
              name="queryCustomized.parameterType"
              label="Parâmetro da consulta *"
              required={true}
            >
              {watch("query.categoryId") === "1" && (
                <>
                  <option value="CPF">CPF</option>
                  <option value="CNPJ">CNPJ</option>
                  <option value="CPF/CNPJ">CPF/CNPJ</option>
                </>
              )}
              {watch("query.categoryId") === "2" && (
                <>
                  <option value="CPF">CPF</option>
                  <option value="CNPJ">CNPJ</option>
                  <option value="CPF/CNPJ">CPF/CNPJ</option>
                </>
              )}
              {watch("query.categoryId") === "3" && (
                <>
                  <option value="PLACA">Placa</option>
                  <option value="PLACA/CHASSI">Placa/Chassi</option>
                  <option value="PLACA/ESTADO">Placa/Estado</option>
                  <option value="PLACA/CHASSI/ESTADO">
                    Placa/Chassi/Estado
                  </option>
                  <option value="CPF/CNPJ">CPF/CNPJ</option>
                </>
              )}
            </SelectInput>
          </div>

          {/* Switches */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="flex items-center mb-1">
              <input
                id="query.isAdditional"
                type="checkbox"
                {...register("query.isAdditional")}
                className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
              />
              <label
                htmlFor="query.isAdditional"
                className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
              >
                É consulta adicional?
              </label>
            </div>
            {!isAdditional && (
              <>
                {" "}
                <div className="flex items-center mb-1">
                  <input
                    id="queryCustomized.showAlertsBoard"
                    type="checkbox"
                    {...register("queryCustomized.showAlertsBoard")}
                    className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                  />
                  <label
                    htmlFor="queryCustomized.showAlertsBoard"
                    className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    Mostrar quadro de avisos na consulta?
                  </label>
                </div>
                <div className="flex items-center mb-1">
                  <input
                    id="queryCustomized.hasIA"
                    type="checkbox"
                    {...register("queryCustomized.hasIA")}
                    className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                  />
                  <label
                    htmlFor="queryCustomized.hasIA"
                    className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    Mostrar sugestão de IA na consulta?{" "}
                    <RiRobot2Line className="inline-block ml-1 text-blue-700 text-base" />
                  </label>
                </div>
                <div className="flex items-center mb-1">
                  <input
                    id="queryCustomized.hasTabs"
                    type="checkbox"
                    {...register("queryCustomized.hasTabs")}
                    className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                  />
                  <label
                    htmlFor="queryCustomized.hasTabs"
                    className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    Exibir resultados em abas
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Campos de retorno */}
          <div className="border border-gray-300 rounded-md p-2">
            <div className="flex gap-2 justify-between items-center mb-4">
              <div className="font-semibold text-sm">Campos de retorno</div>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    append({
                      priority: fields.length + 1,
                      queryId: "",
                      providerId: "",
                      ignoreErrors: true,
                    });
                  }}
                  className="rounded-full w-6 h-6 bg-primary text-white flex items-center justify-center shadow-md"
                >
                  +
                </button>
              </div>
            </div>

            {fields.map((field, index) => {
              const selectedQuery = providers?.find(
                (q) => q.id === Number(watch(`fields[${index}].providerId`))
              );

              return (
                <div
                  key={field.id || index}
                  className={`mb-5 ${
                    index !== fields.length - 1 &&
                    "border-b border-gray-200 pb-3"
                  }`}
                >
                  <div className="flex gap-2 mb-2 items-start">
                    <div className="w-52">
                      <SelectInput
                        register={register}
                        name={`fields[${index}].priority`}
                        label="Ordem"
                        required={true}
                      >
                        {Array.from({ length: 20 }).map((_, i) => (
                          <option key={i} value={i + 1}>
                            {i + 1}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                    <SelectInput
                      register={register}
                      name={`fields[${index}].providerId`}
                      label="Provedor *"
                      required={true}
                    >
                      <option value="">Selecione um Provedor</option>
                      {providers?.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.id} - {provider.name}
                        </option>
                      ))}
                    </SelectInput>
                    <SelectInput
                      register={register}
                      name={`fields[${index}].queryId`}
                      label="Conteúdo *"
                      required={true}
                      error={errors?.fields?.[index]?.queryId?.message}
                    >
                      <option value="">Selecione uma consulta</option>
                      {selectedQuery?.associatedQueries
                        ?.filter(
                          (item) => watch("query.categoryId") == item.categoryId
                        )
                        ?.map((query) => (
                          <option key={query.id} value={query.id}>
                            {query.name} -{" "}
                            {formatToReal(query?.QueryProvider?.price || 0)}
                          </option>
                        ))}
                    </SelectInput>

                    {fields.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          remove(index);
                        }}
                        className="w-6 min-w-6 h-6 mt-3 rounded-full bg-red-600 text-white flex items-center justify-center"
                        title="Remover campo"
                      >
                        –
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col md:flex-row gap-5">
                    <div className="flex items-center mb-1">
                      <input
                        type="checkbox"
                        id={`fields[${index}].ignoreErrors`}
                        {...register(`fields[${index}].ignoreErrors`)}
                        className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                      />
                      <label
                        htmlFor={`fields[${index}].ignoreErrors`}
                        className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Ignorar erro de retorno
                      </label>
                    </div>
                    <div className="flex items-center mb-1">
                      <input
                        type="checkbox"
                        id={`fields[${index}].isUniqueProvider`}
                        {...register(`fields[${index}].isUniqueProvider`)}
                        className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                      />
                      <label
                        htmlFor={`fields[${index}].isUniqueProvider`}
                        className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        É provedor único?
                      </label>
                      <Tooltip
                        id="tooltip-unique-provider"
                        content={
                          <span>
                            Ao assinalar, indica que a consulta será feita{" "}
                            <br />
                            preferencialmente por um provedor específico. <br />
                            Se houver falha nessa busca, o sistema não buscará
                            <br />
                            automaticamente outro provedor disponível para{" "}
                            <br />
                            garantir o retorno dos dados.
                          </span>
                        }
                      />
                      <FaRegQuestionCircle
                        className="text-gray-400 ml-1"
                        data-tooltip-id="tooltip-unique-provider"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ================== Transfer List: Consultas Adicionais ================== */}
          {!isAdditional && (
            <div className="border border-gray-300 rounded-md p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-sm">
                  Consultas adicionais
                </div>
                <small className="text-gray-500">
                  Selecione as consultas adicionais que compõem esta consulta
                  principal
                </small>
              </div>

              {/* Filtro por categoria ausente */}
              {!categoryId && (
                <Alert color="warning" className="mb-3">
                  Selecione a <b>categoria</b> para listar as consultas
                  adicionais disponíveis.
                </Alert>
              )}

              <div className="flex gap-3">
                {/* Esquerda: candidatas */}
                <div className="border rounded-md p-2 flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">
                      Disponíveis ({leftList.length})
                    </span>
                    <input
                      type="text"
                      placeholder="Buscar..."
                      value={searchLeft}
                      onChange={(e) => setSearchLeft(e.target.value)}
                      className="rounded-md border border-gray-300 p-1 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div className="h-52 overflow-auto border rounded">
                    <ul className="text-sm">
                      {leftList.map((q) => (
                        <li
                          key={q.id}
                          className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            checked={leftSelected.includes(q.id)}
                            onChange={(e) => {
                              setLeftSelected((prev) =>
                                e.target.checked
                                  ? [...prev, q.id]
                                  : prev.filter((id) => id !== q.id)
                              );
                            }}
                            id={`left-${q.id}`}
                          />
                          <label
                            htmlFor={`left-${q.id}`}
                            className="cursor-pointer flex-1"
                          >
                            {q.name}
                          </label>
                        </li>
                      ))}
                      {leftList.length === 0 && (
                        <li className="text-xs text-gray-500 px-2 py-2">
                          Nenhum item
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Centro: botões */}
                <div className="flex flex-col items-center justify-center gap-2">
                  <Button
                    size="xs"
                    color="light"
                    onClick={moveRight}
                    disabled={leftSelected.length === 0}
                  >
                    &gt;
                  </Button>
                  <Button
                    size="xs"
                    color="light"
                    onClick={moveAllRight}
                    disabled={leftList.length === 0}
                  >
                    &gt;&gt;
                  </Button>
                  <Button
                    size="xs"
                    color="light"
                    onClick={moveLeft}
                    disabled={rightSelected.length === 0}
                  >
                    &lt;
                  </Button>
                  <Button
                    size="xs"
                    color="light"
                    onClick={moveAllLeft}
                    disabled={selectedAdditionalIds.length === 0}
                  >
                    &lt;&lt;
                  </Button>
                </div>

                {/* Direita: selecionadas */}
                <div className="border rounded-md p-2 flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">
                      Selecionadas ({rightList.length})
                    </span>
                    <input
                      type="text"
                      placeholder="Buscar..."
                      value={searchRight}
                      onChange={(e) => setSearchRight(e.target.value)}
                      className="rounded-md border border-gray-300 p-1 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div className="h-52 overflow-auto border rounded">
                    <ul className="text-sm">
                      {rightList.map((q) => (
                        <li
                          key={q.id}
                          className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            checked={rightSelected.includes(q.id)}
                            onChange={(e) => {
                              setRightSelected((prev) =>
                                e.target.checked
                                  ? [...prev, q.id]
                                  : prev.filter((id) => id !== q.id)
                              );
                            }}
                            id={`right-${q.id}`}
                          />
                          <label
                            htmlFor={`right-${q.id}`}
                            className="cursor-pointer flex-1"
                          >
                            {q.name}
                          </label>
                        </li>
                      ))}
                      {rightList.length === 0 && (
                        <li className="text-xs text-gray-500 px-2 py-2">
                          Nenhum item
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Ordem opcional dos selecionados */}
              {rightList.length > 1 && (
                <small className="text-gray-500 block mt-2">
                  A ordem pode ser definida depois (ex.: via prioridade por
                  vínculo), se necessário.
                </small>
              )}
            </div>
          )}
          {/* ================== /Transfer List ================== */}

          {/* ---- Tags amigáveis (chips) ---- */}
          {!isAdditional && (
            <div className="border border-gray-300 rounded-md p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-semibold text-sm">
                  Tags da consulta{" "}
                  <span className="text-xs text-gray-500">
                    ({selectedTagIds.length} selecionada
                    {selectedTagIds.length === 1 ? "" : "s"})
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Buscar tag..."
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    className="rounded-md border border-gray-300 p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <Button
                    size="xs"
                    color="light"
                    onClick={selectAllFiltered}
                    disabled={isLoadingTags || filteredTags.length === 0}
                  >
                    Selecionar filtradas
                  </Button>
                  <Button
                    size="xs"
                    color="light"
                    onClick={clearAllTags}
                    disabled={selectedTagIds.length === 0}
                  >
                    Limpar
                  </Button>
                </div>
              </div>

              {isLoadingTags ? (
                <div className="text-sm text-gray-500">Carregando tags...</div>
              ) : tagsError ? (
                <Alert color="failure">Erro ao carregar tags.</Alert>
              ) : filteredTags.length === 0 ? (
                <div className="text-sm text-gray-500">
                  Nenhuma tag encontrada.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {filteredTags.map((tag) => {
                    const active = selectedTagIds.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        className={`px-3 py-1 rounded-full border text-sm transition-all
                        ${
                          active
                            ? "bg-cyan-600 text-white border-cyan-600"
                            : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                        }`}
                        title={tag.slug || tag.name}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {submitError && (
            <Alert withBorderAccent color="failure">
              Ops! Ocorreu um erro ao tentar criar uma nova consulta. Tente
              novamente mais tarde.
            </Alert>
          )}
        </form>

        {(isLoadingProviders || isLoadingTags || isLoadingCompanies) && (
          <DrawerLoading />
        )}
      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-1 pt-3 pb-3">
        <Button
          color="gray"
          onClick={() => {
            onClose(false);
            setSelectedTagIds([]);
            setTagSearch("");
            setSelectedCompanyIds([]);
          }}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          onClick={handleSubmit(onSubmit)}
          isProcessing={isSubmitting}
          disabled={isSubmitting}
        >
          Criar consulta
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateQueryForm;
